import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Button,
  Snackbar,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Collapse
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../hooks/useAuth';
import { auditLogService } from '../../services/auditLog';
import CompetitionAccess from '../../components/CompetitionAccess';
import SystemSettingsTab from '../../components/AdminDashboard/SystemSettingsTab';
import AuditLogTab from '../../components/AdminDashboard/AuditLogTab';
import InviteUserDialog from '../../components/AdminDashboard/InviteUserDialog';
import PendingInvitesPanel from '../../components/AdminDashboard/PendingInvitesPanel';
import { getStatusLabel, getStatusColor } from '../../constants/competitionConstants';

const AdminDashboard = () => {
  const { isSuperAdmin } = useAuth();
  const [tab, setTab] = useState(0);
  const [competitions, setCompetitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCompetitions: 0,
    activeCompetitions: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [competitionSearchQuery, setCompetitionSearchQuery] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [expandedCompetitionId, setExpandedCompetitionId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  }, [isSuperAdmin]);

  const loadData = async () => {
    try {
      // Load competitions (exclude deleted) with their config names
      const { data: compsData, error: compsError } = await supabase
        .from('competitions')
        .select(`
          id,
          name,
          status,
          created_at,
          competition_access (
            user_id,
            role
          )
        `)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false });

      if (compsError) throw compsError;

      // Fetch real names from competition_configs (latest revision for each)
      const competitionIds = (compsData || []).map(c => c.id);
      if (competitionIds.length > 0) {
        const { data: configsData } = await supabase
          .from('competition_configs')
          .select('competition_id, content')
          .in('competition_id', competitionIds)
          .order('revision', { ascending: false });

        // Build a map of competition_id -> config data (from latest config)
        const configMap = {};
        (configsData || []).forEach(config => {
          if (!configMap[config.competition_id]) {
            configMap[config.competition_id] = {
              name: config.content?.competition?.name,
              startDate: config.content?.competition?.startDate,
              endDate: config.content?.competition?.endDate,
              status: config.content?.competition?.status
            };
          }
        });

        // Merge config data into competitions
        const compsWithNames = (compsData || []).map(comp => ({
          ...comp,
          displayName: configMap[comp.id]?.name || comp.name || `Competition ${comp.id}`,
          startDate: configMap[comp.id]?.startDate,
          endDate: configMap[comp.id]?.endDate,
          // Prefer config status over competitions table status (they can get out of sync)
          status: configMap[comp.id]?.status || comp.status
        }));

        // Sort by event date (startDate) descending, fallback to created_at
        compsWithNames.sort((a, b) => {
          const dateA = a.startDate ? new Date(a.startDate) : new Date(a.created_at);
          const dateB = b.startDate ? new Date(b.startDate) : new Date(b.created_at);
          return dateB - dateA; // Most recent first
        });

        setCompetitions(compsWithNames);
      } else {
        setCompetitions([]);
      }

      // Load users if superadmin
      if (isSuperAdmin) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select(`
            id,
            email,
            created_at,
            last_sign_in_at,
            user_roles (
              role
            )
          `);

        if (usersError) throw usersError;
        setUsers(usersData);

        // Set dashboard stats
        setStats({
          totalUsers: usersData.length,
          totalCompetitions: compsData.length,
          activeCompetitions: compsData.filter(c => c.status === 'active').length
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole, userEmail) => {
    try {
      const oldRole = users.find(u => u.id === userId)?.user_roles?.role || 'user';

      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId);

      if (error) throw error;

      // Log the change
      await auditLogService.logAction(
        'user.role_changed',
        'user',
        userId,
        { role: oldRole, email: userEmail },
        { role: newRole, email: userEmail }
      );

      // Update local state
      setUsers(prev => prev.map(u =>
        u.id === userId
          ? { ...u, user_roles: { role: newRole } }
          : u
      ));

      setSnackbar({ open: true, message: 'Role updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error updating role:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Filter users by search query
  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary">
            Loading admin dashboard...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch the data
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" sx={{ my: 4 }}>
        Admin Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
          <Tab label="Competition Access" />
          {isSuperAdmin && <Tab label="User Management" />}
          {isSuperAdmin && <Tab label="System Settings" />}
          {isSuperAdmin && <Tab label="Audit Log" />}
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {/* Tab 0: Competition Access */}
          {tab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search competitions..."
                  value={competitionSearchQuery}
                  onChange={(e) => setCompetitionSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 300 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const filtered = competitions.filter(c =>
                      c.displayName?.toLowerCase().includes(competitionSearchQuery.toLowerCase())
                    );
                    return competitionSearchQuery
                      ? `${filtered.length} of ${competitions.length} competitions`
                      : `${competitions.length} competition${competitions.length !== 1 ? 's' : ''}`;
                  })()}
                </Typography>
              </Box>
              {competitions.length === 0 ? (
                <Typography color="text.secondary">
                  No competitions found.
                </Typography>
              ) : (
                <Paper variant="outlined">
                  <List disablePadding>
                    {competitions
                      .filter(c => c.displayName?.toLowerCase().includes(competitionSearchQuery.toLowerCase()))
                      .map((competition, index, filteredArr) => (
                      <Box key={competition.id}>
                        <ListItem
                          sx={{
                            borderBottom: index < filteredArr.length - 1 ? 1 : 0,
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {competition.displayName}
                                </Typography>
                                <Chip
                                  label={getStatusLabel(competition.status)}
                                  size="small"
                                  color={getStatusColor(competition.status)}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {competition.startDate && (
                                  <>Event: {new Date(competition.startDate).toLocaleDateString()} • </>
                                )}
                                Created: {new Date(competition.created_at).toLocaleDateString()}
                                {competition.competition_access?.length > 0 && (
                                  <> • {competition.competition_access.length} user{competition.competition_access.length !== 1 ? 's' : ''}</>
                                )}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Open competition">
                              <IconButton
                                component={RouterLink}
                                to={`/competition/${competition.id}`}
                                size="small"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={expandedCompetitionId === competition.id ? 'Hide access controls' : 'Manage access'}>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedCompetitionId(
                                  expandedCompetitionId === competition.id ? null : competition.id
                                )}
                              >
                                {expandedCompetitionId === competition.id ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItem>
                        <Collapse in={expandedCompetitionId === competition.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                            <CompetitionAccess competitionId={competition.id} />
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}

          {/* Tab 1: User Management */}
          {tab === 1 && isSuperAdmin && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">User Management</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Users: {stats.totalUsers}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteDialogOpen(true)}
                  >
                    Invite User
                  </Button>
                </Box>
              </Box>

              {/* Pending Invites */}
              <Box sx={{ mb: 4 }}>
                <PendingInvitesPanel onRefreshNeeded={loadData} />
              </Box>

              {/* User List */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Existing Users
                </Typography>
                <TextField
                  fullWidth
                  label="Search Users"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Joined</TableCell>
                        <TableCell>Last Active</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.map(user => (
                        <TableRow key={user.id}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Select
                              value={user.user_roles?.role || 'user'}
                              size="small"
                              onChange={(e) => handleRoleChange(user.id, e.target.value, user.email)}
                            >
                              <MenuItem value="user">User</MenuItem>
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="superadmin">Superadmin</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {user.last_sign_in_at ?
                              new Date(user.last_sign_in_at).toLocaleDateString() :
                              'Never'
                            }
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => {
                                // TODO: Implement user details dialog
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}

          {/* Tab 2: System Settings */}
          {tab === 2 && isSuperAdmin && (
            <SystemSettingsTab />
          )}

          {/* Tab 3: Audit Log */}
          {tab === 3 && isSuperAdmin && (
            <AuditLogTab />
          )}
        </Box>
      </Paper>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onInviteSent={loadData}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
